import type { ReactNode } from 'react';

import { Badge, type BadgeVariant } from '@/components/Badge';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { HStack } from '@/components/Layout';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import { Text } from '@/components/Typography/Text';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

/**
 * deprecated な BadgeVariant を除外した型
 *
 * FE側の既存BadgeVariant（destructive / destructiveMuted 除く）に準拠
 */
export type NonDeprecatedBadgeVariant = Exclude<
  BadgeVariant,
  'destructive' | 'destructiveMuted'
>;

/**
 * バッジ選択肢の型
 */
export type BadgeOption = {
  /** 選択値（onChange で返される値） */
  value: string;
  /** 表示テキスト */
  label: string;
  /** バッジのスタイルバリアント */
  variant: NonDeprecatedBadgeVariant;
};

type Props = CFormItemProps & {
  /** バッジに表示するテキスト */
  text: string | null;
  /** バッジのスタイルバリアント */
  variant: NonDeprecatedBadgeVariant;
  /** バッジの先頭に表示するアイコン */
  icon?: IconName;
  /** 編集モード時の選択肢（編集可能にする場合は必須） */
  options?: BadgeOption[];
  /** 現在選択されている値（options の value に対応） */
  value?: string;
  /** 編集モード時の値変更ハンドラ */
  onChange?: (value: string) => void;
  /** 読取時のカスタムコンポーネント */
  readComponent?: ReactNode;
  /** 入力フィールドの幅 */
  inputWidth?: number | string;
};

/**
 * Card-based Form / BadgeItem
 *
 * バッジ表示コンポーネント
 * Confidentiality Level や Sharing Scope などのステータス表示に使用
 * options と onChange を指定することで編集モードに対応
 */
export const CFormBadgeItem = buildCFormItem<Props>((props: Props) => {
  const defaultReadComponent =
    props.text != null ? (
      <Badge variant={props.variant}>
        <HStack className="gap-1 items-center">
          {props.icon && <Icon iconName={props.icon} className="w-3 h-3" />}
          <span>{props.text}</span>
        </HStack>
      </Badge>
    ) : (
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        -
      </Text>
    );

  // options と onChange が指定されている場合は編集可能
  const isEditable = props.options != null && props.onChange != null;

  // 現在選択されている値を特定（value prop を優先、なければ label で検索）
  const currentValue =
    props.value ??
    props.options?.find((opt) => opt.label === props.text)?.value ??
    '';

  // 選択肢を ControlledSelect の形式に変換
  const fetchOptions = props.options
    ? async () =>
        (props.options ?? []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        }))
    : undefined;

  const writeComponent =
    isEditable && props.onChange != null ? (
      <ControlledSelect
        id={props.id}
        value={currentValue}
        onChange={(option) => props.onChange?.(option?.value ?? '')}
        fetchOptions={fetchOptions}
        hideSearch={true}
        allowEmpty={!props.required}
        width={props.inputWidth}
        disabled={props.readonly}
      />
    ) : undefined;

  return (
    <CFormItemBase
      {...props}
      readComponent={props.readComponent ?? defaultReadComponent}
      writeComponent={writeComponent}
    />
  );
});
